import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  ContentLabel,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  ModalButton,
  User
} from 'components/common';
import { CompetenciesProductBadge } from 'components/competencies';

import type {
  GetCompetencyProfilesRes,
  FindMatchingUsersToCompetencyProfile
} from 'shared-types/responses';

interface Props {
  competencyProfile: GetCompetencyProfilesRes['competencyProfiles'][0];
}

const CompetencyProfilesFindMatchingUsers = ({ competencyProfile }: Props) => {
  const { language, messages, theme } = useReduxState();

  const [isVisible, setIsVisible] = useState(false);

  const { data, loading } = useApiData<FindMatchingUsersToCompetencyProfile>({
    url: `/admin/competency-profiles/${competencyProfile.id}/users`,
    onlyIf: isVisible
  });

  return (
    <ModalButton
      buttonIsDropdown={true}
      icon="user"
      modalContentStyle={{ padding: 0 }}
      modalProps={{
        closeButton: true,
        title:
          competencyProfile.titleLocales?.[language] || competencyProfile.title,
        width: 'medium'
      }}
      onModalHide={() => setIsVisible(false)}
      onModalShow={() => setIsVisible(true)}
      text={messages.competencyProfilesShowMatchingUsers}
    >
      <div className="content">
        <div className="header">
          <ContentLabel
            horizontal={true}
            horizontalBreakpoint={breakpoints.small}
            label={messages.analyticsCompetencies}
          >
            <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
              {competencyProfile.competencies.map((competency) => (
                <CompetenciesProductBadge
                  key={`${competency.id}-${competency.level?.id || 0}`}
                  competency={competency}
                />
              ))}
            </FlexContainer>
          </ContentLabel>
        </div>

        {loading ? (
          <LoadingIndicator />
        ) : data?.users?.length ? (
          <div className="users">
            {data.users.map((user) => (
              <div className="user" key={user.id}>
                <User enableProfileLink={true} user={user} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyIndicator icon="user" title={messages.settingsNoUsersFound} />
        )}

        <style jsx>{`
          .header {
            border-bottom: 1px solid ${theme.lightGreyDark};
            padding: 20px 15px;
          }
          .user {
            padding: 12px 15px;
          }
          .user:not(:first-child) {
            border-top: 1px solid ${theme.lightGrey};
          }
        `}</style>
      </div>
    </ModalButton>
  );
};

export default CompetencyProfilesFindMatchingUsers;
